import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOTTLENECKS = ["Energy", "Sleep", "Burnout", "Focus"];

const initialForm = {
  full_name: "",
  role_org: "",
  bottleneck: "Energy",
  stress_voltage: 5,
  email: "",
  phone: "",
};

const inputCls =
  "w-full bg-ink border border-[#2A2A2E] focus:border-gold/60 text-neutral-100 placeholder:text-neutral-600 px-4 py-3.5 text-sm font-light outline-none transition-colors duration-300";

export default function IntakeForm() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        stress_voltage: Number(form.stress_voltage),
      });
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Transmission failed. Please verify your details and retry."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="intake-application"
      className="relative bg-ink-2 py-24 sm:py-32 lg:py-40 border-t border-gold/10 overflow-hidden"
      data-testid="intake-section"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(184,115,51,0.09),transparent_55%)]" />
      <div className="relative max-w-3xl mx-auto px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="text-center mb-14 sm:mb-16"
        >
          <p className="text-[11px] uppercase tracking-[0.35em] text-copper-hover mb-5" data-testid="intake-eyebrow">
            By Application Only — 12 Executive Clients Per Quarter
          </p>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-gold font-medium tracking-tight leading-[1.12]" data-testid="intake-heading">
            Private Intake Application
          </h2>
          <div className="gold-divider mt-8 max-w-sm mx-auto" />
          <p className="text-sm sm:text-base text-neutral-500 font-light mt-6 max-w-xl mx-auto leading-relaxed">
            This is not a newsletter. Every application is reviewed personally.
            If your biology qualifies, the concierge desk will contact you
            within 48 hours.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mb-10 sm:mb-12 text-center"
        >
          <a
            data-testid="preorder-book-button"
            href="https://amazon.com/dp/B0HFD2BTGK"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-copper hover:bg-copper-hover text-white font-bold uppercase tracking-[0.2em] text-xs sm:text-sm px-8 sm:px-12 py-4 sm:py-5 transition-colors duration-300 shadow-[0_0_30px_rgba(184,115,51,0.35)] hover:shadow-[0_0_44px_rgba(184,115,51,0.55)] active:scale-[0.98]"
          >
            Explore the 400-Page Nutritional System (Pre-Order)
          </a>
        </motion.div>

        <AnimatePresence mode="wait">
          {submitted ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="border border-gold/30 bg-ink-3 p-10 sm:p-14 text-center"
              data-testid="intake-success-message"
            >
              <CheckCircle2 size={40} strokeWidth={1.25} className="text-gold mx-auto mb-6" />
              <h3 className="font-serif text-2xl sm:text-4xl text-gold mb-4">Application Received.</h3>
              <p className="text-sm sm:text-base text-neutral-400 font-light leading-relaxed max-w-md mx-auto">
                Your file has entered private review. The executive concierge
                will respond within 48 hours. Protect your voltage in the
                meantime.
              </p>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              onSubmit={onSubmit}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
              className="border border-[#26262A] bg-ink-3/70 backdrop-blur-md p-7 sm:p-12 space-y-7"
              data-testid="intake-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <div className="space-y-2.5">
                  <label htmlFor="intake-name-input" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Full Name</label>
                  <input id="intake-name-input" data-testid="intake-name-input" required value={form.full_name} onChange={set("full_name")} placeholder="Alexander Pierce" className={inputCls} />
                </div>
                <div className="space-y-2.5">
                  <label htmlFor="intake-role-input" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Corporate Role / Organization</label>
                  <input id="intake-role-input" data-testid="intake-role-input" required value={form.role_org} onChange={set("role_org")} placeholder="CEO, Meridian Capital" className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <div className="space-y-2.5">
                  <label htmlFor="intake-bottleneck-select" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Primary Biological Bottleneck</label>
                  <select id="intake-bottleneck-select" data-testid="intake-bottleneck-select" value={form.bottleneck} onChange={set("bottleneck")} className={inputCls}>
                    {BOTTLENECKS.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2.5">
                  <label htmlFor="intake-stress-select" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
                    Current Stress Voltage — <span className="text-gold">{form.stress_voltage}/10</span>
                  </label>
                  <input
                    id="intake-stress-select"
                    data-testid="intake-stress-select"
                    type="range"
                    min="1"
                    max="10"
                    step="1"
                    value={form.stress_voltage}
                    onChange={set("stress_voltage")}
                    className="lux-range w-full mt-4"
                  />
                  <div className="flex justify-between text-[9px] uppercase tracking-[0.18em] text-neutral-600">
                    <span>Regulated</span>
                    <span>Critical</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                <div className="space-y-2.5">
                  <label htmlFor="intake-email-input" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Direct Email</label>
                  <input id="intake-email-input" data-testid="intake-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="a.pierce@meridian.com" className={inputCls} />
                </div>
                <div className="space-y-2.5">
                  <label htmlFor="intake-phone-input" className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">Private Phone</label>
                  <input id="intake-phone-input" data-testid="intake-phone-input" type="tel" required value={form.phone} onChange={set("phone")} placeholder="+1 212 555 0148" className={inputCls} />
                </div>
              </div>

              <div className="pt-3">
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="intake-submit-button"
                  className="w-full bg-copper hover:bg-copper-hover disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold uppercase tracking-[0.24em] text-xs sm:text-sm py-4.5 sm:py-5 px-8 py-4 transition-colors duration-300 shadow-[0_0_28px_rgba(184,115,51,0.3)] hover:shadow-[0_0_40px_rgba(184,115,51,0.5)] active:scale-[0.99] flex items-center justify-center gap-3"
                >
                  {submitting && <Loader2 size={16} className="animate-spin" />}
                  {submitting ? "Transmitting…" : "Submit Private Application"}
                </button>
                <p className="text-center text-[10px] uppercase tracking-[0.2em] text-neutral-600 mt-5">
                  Strictly confidential. Reviewed personally by Joe Fenix.
                </p>
              </div>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
